import PropTypes from 'prop-types'
import React from 'react'

import { BugsnagErrorBoundary } from 'lib/bugsnag'
import logError from 'lib/logError'
import trackEvent from 'lib/trackEvent'
import { openUrlInDefaultBrowser } from 'lib/config/appBridge'

import { Button } from '../Button'
import { Icon, icons } from '../Icon'
import { Text } from '../Text'
import styles from './ErrorBoundary.module.scss'

const WA_URL = `https://api.whatsapp.com/send?phone=${window?.globals?.businessNo}&text=Hi%20Zoko!%20Something%20went%20wrong%20in%20the%20Zoko%20dashboard.`

const _FallBackUI = ({ fallBackMessage, errorUI }) => {
  const isWhiteLabelled = window?.globals?.isWhiteLabelled
  const text = `errorBoundary.${
    !isWhiteLabelled ? 'somethingWentWrong' : 'unknown'
  }`
  const _comp = () => {
    const Comp = (
      <div
        className={styles.wrapper}
        data-testid="auto-id-14f05b43-cf19-42a8-97f4-a9b547d6aa47"
      >
        <div
          className={styles.icon}
          data-testid="auto-id-93809792-6fea-49a5-8523-875beb778812"
        >
          <Icon source={icons.somethingWentWrong} size="var(--space-15)" />
        </div>
        <Text
          translate
          className={styles.heading}
          data-testid="auto-id-3e9888d6-4165-4138-acba-15fd90c33f81"
        >
          {fallBackMessage || text}
        </Text>
        {!isWhiteLabelled ? (
          <>
            <Text
              translate
              className={styles.description}
              data-testid="auto-id-1ca75608-27db-4d34-8a44-275c60bacd4f"
            >
              errorBoundary.technicalStaffWorking
            </Text>
            <Button
              variant="text"
              onClick={(e) =>
                !openUrlInDefaultBrowser(WA_URL, e) &&
                window.open(WA_URL, '_blank').focus()
              }
              data-testid="auto-id-00e09677-9a83-4889-a003-ed70f13170df"
            >
              <Text
                translate
                data-testid="auto-id-7d8fb2e4-41f1-4500-8bdc-d04aa14b1643"
              >
                errorBoundary.whatsappSupport
              </Text>
            </Button>
          </>
        ) : null}
        <Button
          onClick={() => window.location.reload()}
          variant="text"
          data-testid="auto-id-ef672b59-045a-4c66-82df-b038d218266a"
        >
          <Text
            translate
            data-testid="auto-id-cfa46279-264e-4a37-bbb9-e25f16ab0583"
          >
            reload
          </Text>
        </Button>
      </div>
    )
    return Comp
  }
  return errorUI ? errorUI : _comp
}

_FallBackUI.propTypes = {
  whiteLabelled: PropTypes.bool,
  fallBackMessage: PropTypes.string,
}

export const ErrorBoundary = ({
  children,
  componentName,
  fallBackMessage,
  errorUI,
}) => {
  const isWhiteLabelled = window?.globals?.isWhiteLabelled
  return (
    <BugsnagErrorBoundary
      FallbackComponent={_FallBackUI({
        isWhiteLabelled,
        fallBackMessage,
        errorUI,
      })}
      onError={(event) => {
        logError(event, 'APP_CRASH', { componentName, event, isWhiteLabelled })
        window.console.error('APP_CRASH', componentName, event, isWhiteLabelled)
        trackEvent(
          trackEvent.modules.service,
          trackEvent.events.app_crash,
          event
        )
        event.severity = 'error'
        event.context = 'BugsnagErrorBoundary app_crash'
        return event.addMetadata('remarks', {
          componentName,
          logger: 'ErrorBoundary',
          event,
          isWhiteLabelled,
          page: window.location.href,
        })
      }}
    >
      {children}
    </BugsnagErrorBoundary>
  )
}

export default ErrorBoundary

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  componentName: PropTypes.string.isRequired,
  fallBackMessage: PropTypes.string,
  errorUI: PropTypes.node,
}
